import { useState, useMemo, useCallback, useEffect, useLayoutEffect } from "react";
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    Radio,
    Button,
    Stack,
    IconButton,
    alpha,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import {
    useGetCountryListQuery,
    useGetForeignStates2Query,
    useGetCityListByProvinceOrStates2Query,
    useGetCountiesByStateMutation,
} from "Services/CountryService";
import useIsUsAccount from "hooks/useIsUsAccount";
import { extractStateOrProvinceFromAbbr } from "views/document-generator-v2/utils";
import MultiSelectDropdown from "components/MultiSelectDropdown";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
    selectGasServedAreaType,
    setGasServedAreaType,
    selectGasServedSelectedAreas,
    setGasServedSelectedAreas,
    selectGasServedHasSelectedAreas,
    clearGasServedSelectedAreas,
} from "views/shared/organization-profile-stepper/slice";

import { buildAddedGeographicAreas } from "utils/buildAddedGeographicAreas";
import { showErrorToaster } from "store/Shared.Slice";

const TypeOptionCard = ({ title, subtitle, selected, onClick }) => (
    <Box
        onClick={onClick}
        sx={{
            border: "1.5px solid",
            borderColor: selected ? "secondary.main" : "#E2E8F0",
            backgroundColor: selected ? "secondary.light" : "#FFFFFF",
            borderRadius: 2.5,
            p: 2,
            cursor: "pointer",
            display: "flex",
            gap: 1.5,
        }}
    >
        <Radio checked={selected} color="secondary" sx={{ p: 0 }} />
        <Box>
            <Typography
                variant="subtitle1"
                color="text.mediumDark"
                sx={{ fontWeight: 600, fontSize: "0.95rem" }}
            >
                {title}
            </Typography>
            <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: "0.825rem" }}
            >
                {subtitle}
            </Typography>
        </Box>
    </Box>
);

export const AreaTag = ({ area, onRemove }) => (
    <Box
        sx={{
            display: "inline-flex",
            alignItems: "center",
            backgroundColor: "secondary.light",
            borderRadius: 5,
            p: 1.2,
            gap: 1.2,
            border: (theme) =>
                `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
        }}
    >
        <Box
            sx={{
                backgroundColor: "secondary.main",
                color: "#FFFFFF",
                fontSize: "0.68rem",
                fontWeight: 600,
                px: 1.2,
                py: 0.4,
                borderRadius: 1,
                lineHeight: 1.2,
            }}
        >
            {area.scope === "country" && "COUNTRY"}
            {area.scope === "region" && "REGION"}
            {area.scope === "city" && "CITY"}
            {area.scope === "county" && "COUNTY"}
        </Box>

        <Typography variant="body2" sx={{ color: "rgb(0 83 75)" }}>
            {area.scope === "country" && area.country}
            {area.scope === "region" && area.region}
            {area.scope === "city" && `${area.city}, ${area.region}`}
            {area.scope === "county" && `${area.county}, ${area.region}`}
        </Typography>
        {onRemove && (
            <IconButton
                size="small"
                onClick={() => onRemove(area.id)}
                sx={{ color: "rgb(0 83 75)", p: 0.2 }}
            >
                <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
        )}
    </Box>
);

// Country/state/city area picker (entire country vs. specific region/city), shared between the
// Organization Profile stepper and the User Project "Geographic Areas Served" field. Presentational:
// takes the current areas and reports the full next array via onChange, same convention as
// AutocompleteWithTags, instead of owning any Redux state itself.
const GeographicAreaPicker = ({ addedAreas = [], onChange, showAddedAreas = true, addAreaRef }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isUs = useIsUsAccount();

    const selectedType = useSelector(selectGasServedAreaType);
    const {
        selectedCountries,
        selectedRegions,
        selectedCities,
        selectedCounties,
    } = useSelector(selectGasServedSelectedAreas);
    const hasSelectedAreas = useSelector(selectGasServedHasSelectedAreas);

    const [countyOptions, setCountyOptions] = useState([]);
    const [isFetchingCounties, setIsFetchingCounties] = useState(false);

    const { isLoading: isCountryLoading, data: countries = [] } =
        useGetCountryListQuery();
    const { data: regions = [], isLoading: isRegionsLoading } =
        useGetForeignStates2Query(isUs ? "US" : "CA");
    const { data: cities = [], isFetching } =
        useGetCityListByProvinceOrStates2Query(selectedRegions);
    const [getCountiesByState] = useGetCountiesByStateMutation();

    const setSelectedArea = (key, value) => {
        dispatch(setGasServedSelectedAreas({ key, value}));
    }

    // Counties (backed by the seeded UsCity table) only exist for US states, mirroring the same
    // isUs gate SimilarCharitiesTagInput uses for its county filter. Fetch the distinct county
    // list per selected state and flatten into options that keep the owning state attached, since
    // the same county name can recur across states.
    useEffect(() => {
        if (!isUs || selectedRegions.length === 0) {
            setCountyOptions([]);
            setSelectedArea("selectedCounties", []);
            return;
        }

        let cancelled = false;
        setIsFetchingCounties(true);

        Promise.all(
            selectedRegions.map((stateCode) =>
                getCountiesByState(stateCode)
                    .unwrap()
                    .then((counties) =>
                        counties.map((countyName) => ({
                            key: stateCode + "|" + countyName,
                            value: countyName,
                            stateCode,
                            region:
                                regions.find((s) => s.code === stateCode)
                                    ?.description ?? stateCode,
                        }))
                    )
                    .catch(() => [])
            )
        ).then((results) => {
            if (cancelled) return;
            setCountyOptions(results.flat());
            setIsFetchingCounties(false);
        });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isUs, selectedRegions]);

    useEffect(() => {
        return () => {
            dispatch(clearGasServedSelectedAreas());
        }
    }, [dispatch]);

    const filterAvailable = useCallback(
        (items, keySelector = (item) => item) => {
            const existingKeys = new Set(
                addedAreas.map((a) => {
                    if (a.scope === "country") return a.country;
                    if (a.scope === "region") return a.region;
                    if (a.scope === "city") return a.region + "|" + a.city;
                    if (a.scope === "county")
                        return a.region + "|county|" + a.county;
                    return null;
                })
            );
            return items.filter((item) => !existingKeys.has(keySelector(item)));
        },
        [addedAreas]
    );

    const availableCountries = useMemo(
        () => filterAvailable(countries, (item) => item.description),
        [countries, filterAvailable]
    );

    const availableCities = useMemo(
        () =>
            filterAvailable(
                cities,
                (item) =>
                    extractStateOrProvinceFromAbbr(item.secondaryAction) +
                    "|" +
                    item.value
            ),
        [cities, filterAvailable]
    );

    const availableCounties = useMemo(
        () =>
            filterAvailable(
                countyOptions,
                (item) => item.region + "|county|" + item.value
            ),
        [countyOptions, filterAvailable]
    );

    const prepareAddArea = useCallback(() => buildAddedGeographicAreas({
        addedAreas, selectedType, selectedCountries, selectedRegions,
        selectedCities, selectedCounties, countries, regions, cities, countyOptions,
        isUs, extractRegion: extractStateOrProvinceFromAbbr,
    }), [addedAreas, selectedType, selectedCountries, selectedRegions,
        selectedCities, selectedCounties, countries, regions, cities, countyOptions, isUs]);

    useLayoutEffect(() => {
        if (!addAreaRef) return;
        addAreaRef.current = prepareAddArea;
        return () => { addAreaRef.current = null; };
    }, [addAreaRef, prepareAddArea]);

    const handleAddArea = () => {
        try {
            const nextAreas = prepareAddArea();
            onChange?.(nextAreas);
            if (selectedType === "entire") {
                setSelectedArea("selectedCountries", []);
            } else {
                setSelectedArea("selectedRegions", []);
                setSelectedArea("selectedCities", []);
                setSelectedArea("selectedCounties", []);
            }
        } catch {
            dispatch(showErrorToaster(t("messages.geographic_areas_add_failed")));
        }
    };

    const handleRemoveArea = (idToRemove) => {
        onChange?.(addedAreas.filter((item) => item.id !== idToRemove));
    };

    const isAddDisabled =
        selectedType === "entire"
            ? selectedCountries.length === 0
            : selectedRegions.length === 0 &&
              selectedCities.length === 0 &&
              selectedCounties.length === 0;

    return (
        <Box>
            <Card
                variant="outlined"
                sx={{ borderRadius: 3, borderColor: "#E2E8F0", mb: showAddedAreas ? 4 : 0 }}
            >
                <CardContent sx={{ p: 3.5 }}>
                    {/* Type Switcher */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6}>
                            <TypeOptionCard
                                title={t("labels.specific_area")}
                                subtitle={t("labels.a_region_a_city_or_both", {
                                    orgRegion: isUs ? "state" : "province",
                                })}
                                selected={selectedType === "specific"}
                                onClick={() => dispatch(setGasServedAreaType("specific"))}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TypeOptionCard
                                title={t("labels.entire_country")}
                                subtitle={t(
                                    "labels.everywhere_within_one_country"
                                )}
                                selected={selectedType === "entire"}
                                onClick={() => dispatch(setGasServedAreaType("entire"))}
                            />
                        </Grid>
                    </Grid>

                    {/* Conditional Inputs */}
                    {selectedType === "entire" ? (
                        <Box sx={{ mb: 3 }}>
                            <MultiSelectDropdown
                                label={t("labels.country")}
                                placeholder={t("labels.select_a_country")}
                                value={selectedCountries}
                                options={availableCountries}
                                getOptionLabel={(c) => c.description}
                                getOptionValue={(c) => c.description}
                                onChange={(e) =>
                                    setSelectedArea("selectedCountries", e.target.value)
                                }
                                loading={isCountryLoading}
                                required
                            />
                        </Box>
                    ) : (
                        <Box sx={{ mb: 2 }}>
                            <Grid container spacing={2} sx={{ mb: 1.5 }}>
                                <Grid item xs={12} sm={isUs ? 4 : 6}>
                                    <MultiSelectDropdown
                                        label={
                                            isUs
                                                ? t("labels.state")
                                                : t("labels.province")
                                        }
                                        placeholder={
                                            isUs
                                                ? t("labels.all_states")
                                                : t("labels.all_provinces")
                                        }
                                        value={selectedRegions}
                                        options={regions}
                                        getOptionLabel={(s) => s.description}
                                        getOptionValue={(s) => s.code}
                                        onChange={(e) =>
                                            setSelectedArea("selectedRegions", e.target.value)
                                        }
                                        loading={isRegionsLoading}
                                    />
                                </Grid>
                                {isUs && (
                                    <Grid item xs={12} sm={4}>
                                        <MultiSelectDropdown
                                            label={t("labels.county")}
                                            placeholder={t(
                                                "labels.all_counties"
                                            )}
                                            value={selectedCounties}
                                            options={availableCounties}
                                            getOptionLabel={(c) => c.value}
                                            getOptionValue={(c) => c.key}
                                            onChange={(e) =>
                                                setSelectedArea("selectedCounties", e.target.value)
                                            }
                                            disabled={
                                                selectedRegions.length === 0
                                            }
                                            loading={isFetchingCounties}
                                        />
                                    </Grid>
                                )}
                                <Grid item xs={12} sm={isUs ? 4 : 6}>
                                    <MultiSelectDropdown
                                        label={t("labels.city")}
                                        placeholder={t("labels.all_cities")}
                                        value={selectedCities}
                                        options={availableCities}
                                        getOptionLabel={(s) => s.value}
                                        getOptionValue={(s) => s.value}
                                        onChange={(e) =>
                                            setSelectedArea("selectedCities", e.target.value)
                                        }
                                        loading={isFetching}
                                    />
                                </Grid>
                            </Grid>
                            <Typography variant="caption" fontSize="0.75rem">
                                {t("messages.specific_area_description", {
                                    orgCountry: isUs
                                        ? "United States"
                                        : "Canada",
                                })}
                            </Typography>
                        </Box>
                    )}

                    {/* Add Area Action */}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            mt: 2,
                        }}
                    >
                        <Button
                            variant="outlined"
                            color="secondary"
                            startIcon={<AddIcon />}
                            onClick={handleAddArea}
                            disabled={isAddDisabled}
                            sx={{
                                borderRadius: 2,
                                textTransform: "none",
                                fontWeight: 600,
                                "& .MuiButton-startIcon": {
                                    animation: hasSelectedAreas
                                        ? "wiggle 1s infinite ease-in-out"
                                        : "none",
                                },
                                animation: hasSelectedAreas
                                ? "pulseRing 3s infinite ease-out"
                                : "none",
                            }}
                        >
                            {t("labels.add_area")}
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {showAddedAreas && (
                <Box sx={{ mb: 6 }}>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ mb: 2 }}
                    >
                        <Typography
                            variant="body1"
                            sx={{
                                fontWeight: 600,
                                color: "#334155",
                                textTransform: "uppercase",
                            }}
                        >
                            {t("labels.areas_added")}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontWeight: 400 }}
                        >
                            {addedAreas.length} added
                        </Typography>
                    </Stack>

                    <Stack direction="row" flexWrap="wrap" gap={1.5}>
                        {addedAreas.map((area) => (
                            <AreaTag
                                key={area.id}
                                area={area}
                                onRemove={handleRemoveArea}
                            />
                        ))}
                    </Stack>
                </Box>
            )}
        </Box>
    );
};

export default GeographicAreaPicker;
